import { onForegroundMessage } from 'config/firebase';
import useUser from 'hooks/useUser';
import { disconnectSocket, getSocket } from 'lib/socket';
import LogoutConfirmationModal from 'pages/dashboard-overview/components/ConfirmLogoutModal';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetNotificationByUserQuery,
  useGetNotificationQuery,
} from 'redux/features/notificationApi';
import { useLogoutMutation } from 'redux/features/users/authApi';
import { clearStore } from 'redux/store';

import Icon from '../AppIcon';
import NotificationSystem from './NotificationSystem';

const Header = ({ hideSearchbar }) => {
  const navigate = useNavigate();
  const logout = useLogoutMutation()[0];
  const socket = getSocket();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const user = useUser();
  const userRole = user?.role;
  const isCheckedIn = user?.session_status === 'ACTIVE';
  const userName =
    user?.role === 'EMPLOYEE' ? user?.first_name + ' ' + user?.last_name || 'User' : 'ADMIN';
  const employeeId = user?.employee_id || 'N/A';

  const handleSearchClick = () => {
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      ctrlKey: true,
      bubbles: true,
    });
    document.dispatchEvent(event);
  };

  useEffect(() => {
    onForegroundMessage().then((payload) => {
      if (payload.data) {
        alert(`Notification: ${payload.data.title} - ${payload.data.body}`);
      }
    });
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('forceLogout', () => {
        disconnectSocket();
        navigate('/login-register');
      });
      return () => {
        socket.off('forceLogout');
      };
    }
  }, [socket, navigate]);

  const handleLogout = async () => {
    await logout();
    disconnectSocket();
    clearStore();
    navigate('/login-register');
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleLogoutClick = () => {
    if (isCheckedIn) {
      setShowLogoutModal(true);
    } else {
      handleLogout();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-surface border-b border-border z-100 ">
      <div className="flex items-center justify-between h-full px-6 min-h-0">
        {/* Left Section - Title */}
        <div className="flex items-center space-x-4">
          <div
            className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity duration-200"
            onClick={() => window.dispatchEvent(new CustomEvent('logoClick'))}
          >
            <div className="w-8 h-8  rounded-lg flex items-center justify-center sm:hidden">
              <img
                src="/assets/images/qodors-mobile.png"
                alt="Qodors — Employee Management System"
                className="h-12 object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <img
                src="/assets/images/qodors-desktop.png"
                alt="Qodors — Employee Management System"
                className="h-12 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Center Section - Search */}

        {!hideSearchbar && (
          <div className="flex-1 max-w-md mx-4 lg:mx-8">
            <div className="relative cursor-pointer" onClick={handleSearchClick}>
              <div className="w-full bg-secondary-50 border border-border rounded-lg px-3 lg:px-4 py-2 flex items-center space-x-2 lg:space-x-3 hover:bg-secondary-100 transition-colors duration-200 h-10 min-h-0">
                <Icon name="Search" size={16} color="#64748B" className="flex-shrink-0" />
                <span className="text-sm text-secondary-500 flex-1 text-left truncate">
                  <span className="hidden sm:inline">Search tasks, projects... (Ctrl+K)</span>
                  <span className="sm:hidden">Search...</span>
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Right Section - Actions & User */}
        <div className="flex items-center space-x-4">
          {user?.role && (
            <NotificationSystem
              getNotification={
                user?.role === 'ADMIN' ? useGetNotificationQuery : useGetNotificationByUserQuery
              }
            />
          )}
          <div className="flex items-center space-x-4">
            {/* User Info */}
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    userRole === 'ADMIN' ? 'bg-primary' : 'bg-success'
                  }`}
                >
                  <span className="text-sm font-medium text-white">
                    {userName.charAt(0).toUpperCase()}
                  </span>
                  {/* Status Indicator */}
                  {userRole === 'EMPLOYEE' && (
                    <div
                      className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
                        user?.session_status === 'ACTIVE' ? 'bg-success animate-pulse' : 'bg-error'
                      }`}
                      title={isCheckedIn ? 'Currently Checked In' : 'Currently Checked Out'}
                    ></div>
                  )}
                </div>
              </div>

              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-text-primary">{userName}</p>
                <p className="text-xs text-text-secondary">{employeeId}</p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogoutClick} // Show the confirmation modal on click
              className="flex items-center justify-center w-10 h-10 text-error hover:bg-error-50 rounded-lg transition-colors duration-200"
              title="Sign Out"
            >
              <Icon name="LogOut" size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Logout Modal */}
      {showLogoutModal && (
        <LogoutConfirmationModal
          onConfirm={handleLogout} // Proceed with logout
          onCancel={handleCancelLogout} // Close the modal
          isCheckedIn={isCheckedIn}
        />
      )}
    </header>
  );
};

export default Header;
